import { SubmitHandler, useForm } from "react-hook-form";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, Typography } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { HighlightOffRounded } from "@mui/icons-material";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useClustersRepository } from "../../../api/repositories/ClustersRepository";
import { useSetRecoilState } from "recoil";
import { STATE_CLUSTERS_TABLE_UPDATING } from "../constants/ClustersRecoil";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { ID_LICENSEES, ID_MANDATORY } from "../constants/ClusterMSTypes";
import { ID_ACTIVE, ID_INACTIVE } from "../constants/ClusterStatuses";
import { asEndOfDay, asStartOfDay } from "../../../utils/FinanceDateUtils";
import FormTextField from '../../../experiments/forms/FormTextField';
import FormSelect from "../../../experiments/forms/FormSelect";
import FormDatePicker from "../../../experiments/forms/FormDatePicker";
import { useHasScope } from "../../../../../config/globalConfig";
import { ACTIVE_DEACTIVE_CLUSTER } from "../../../utils/FinanceScope";

interface ClusterModalInputs {
    name: string,
    type: string,
    validDateFrom: Date,
    validDateTo: Date,
    status: string
}

const ClustersModal = (props: any) => {
    const { t } = useTranslation('finance', { keyPrefix: 'clusters' });
    const { updateCluster } = useClustersRepository();
    const setUpdating = useSetRecoilState(STATE_CLUSTERS_TABLE_UPDATING);
    const hasScope = useHasScope;
 
    const schema = Yup.object().shape({
        name: Yup.string(),
        type: Yup.string(),
        validDateFrom: Yup.date().test("testFrom", t('cluster-modal.form.errors.invalid-date'), (value, context) => {
            return !value || !context.parent.validDateTo || (value <= context.parent.validDateTo);
        }).typeError(t('cluster-modal.form.errors.invalid-date')),
        validDateTo: Yup.date().test("testTo", t('cluster-modal.form.errors.invalid-date'), (value, context) => {
            return !value || !context.parent.validDateFrom || (value >= context.parent.validDateFrom);
        }).typeError(t('cluster-modal.form.errors.invalid-date')),
        status: Yup.string()
    });

    const defaultValues = {
        name: "",
        type: "",
        validDateFrom: new Date(),
        validDateTo: new Date(),
        status: ""
    };

    const { handleSubmit, reset, watch, setValue, control, clearErrors } = useForm<ClusterModalInputs>({
        resolver: yupResolver(schema),
        defaultValues: defaultValues
    });

    const types = [
        {
            value: ID_MANDATORY,
            label: t('ms-types.' + ID_MANDATORY)
        },
        {
            value: ID_LICENSEES,
            label: t('ms-types.' + ID_LICENSEES)
        }
    ];

    const statuses = [
        {
            value: ID_ACTIVE,
            label: t('statuses.' + ID_ACTIVE),
        },
        {
            value: ID_INACTIVE,
            label: t('statuses.' + ID_INACTIVE),
        }
    ];

    const watcher = watch();

    const setInitialValues = () => {
        setValue('name', props.cluster.name);
        setValue('type', props.cluster.msTypology);
        setValue('validDateTo', props.cluster.validTo);
        setValue('validDateFrom', props.cluster.validFrom);
        setValue('status', props.cluster.validStatus);
    };

    useEffect(() => {
        if (props.open && props.cluster) {
            setInitialValues();
        }
    }, [props.open]);

    const onSubmit: SubmitHandler<ClusterModalInputs> = (data: any) => {
        if (data) {
            updateCluster(props.cluster.id,
                data.status,
                data.type,
                data.name,
                asStartOfDay(data.validDateFrom) || props.cluster.validDateFrom,
                asEndOfDay(data.validDateTo) || props.cluster.validDateTo
            ).then(() => {
                props.onClose();
                resetForm();
                setUpdating(true);
            });
        }
    }

    const resetForm = () => {
        reset(defaultValues);
        clearErrors();
    };

    return (
        <Dialog maxWidth={"lg"} disableEscapeKeyDown fullWidth={true} open={props.open} onClose={() => {
            return;
        }}>
            <DialogTitle>
                <Typography fontWeight={"bold"} color={"primary"}>
                    {t('cluster-modal.title')}
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={() => {
                        props.onClose();
                        resetForm();
                    }}
                    color={"primary"}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                    }}
                >
                    <HighlightOffRounded/>
                </IconButton>
            </DialogTitle>
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogContent>
                    <Grid container spacing={2} sx={{ padding: "15px" }} alignItems={"center"}>
                        <Grid item xs={12}>
                            <FormTextField
                                name={'name'}
                                control={control}
                                props={{
                                    label: t('cluster-modal.form.labels.name'),
                                    fullWidth: true
                                }}

                            />
                        </Grid>
                        <Grid item md={3} sm={6} xs={12}>
                            <FormSelect
                                name={'type'}
                                control={control}
                                props={{
                                    label: t('cluster-modal.form.labels.type'),
                                    fullWidth: true,
                                }}
                                items={
                                    types.map((option: any) => (
                                        <MenuItem key={option.value}
                                                  value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))
                                }
                            />
                        </Grid>
                        <Grid item md={3} sm={6} xs={12}>
                            <FormDatePicker
                                name={"validDateFrom"}
                                control={control}
                                props={{
                                    label: t('cluster-modal.form.labels.valid-from'),
                                    maxDate: watcher.validDateTo || undefined
                                }}
                            />
                        </Grid>
                        <Grid item md={3} sm={6} xs={12}>
                            <FormDatePicker
                                name={"validDateTo"}
                                control={control}
                                props={{
                                    label: t('cluster-modal.form.labels.valid-to'),
                                    minDate: watcher.validDateFrom || undefined
                                }}
                            />
                        </Grid>
                        <Grid item md={3} sm={6} xs={12}>
                            <FormSelect
                                name={"status"}
                                control={control}
                            
                                props={{
                                    label: t('cluster-modal.form.labels.status'),
                                    fullWidth: true,
                                    disabled:  !hasScope(ACTIVE_DEACTIVE_CLUSTER)
                                }}
                                items={statuses.map((option: any) => (
                                    <MenuItem key={option.value}
                                              value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ padding: "10px" }}>
                    <Button variant={"outlined"} color={"accent"}
                            onClick={() => {
                                setInitialValues()
                                props.onClose();
                            }}>{t('cluster-modal.buttons.reset')}</Button>
                    <Button color={"accent"} type="submit">{t('cluster-modal.buttons.apply')}</Button>
                </DialogActions>
            </form>
        </Dialog>
    )
}

export default ClustersModal;

